<script setup lang="ts">
import { computed } from 'vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import HistoryMessageContent from './HistoryMessageContent.vue';
import HistoryAttachments from './HistoryAttachments.vue';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { MessageEditDiffItem } from 'dashboard/helper/messageHistoryHelper';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  item: MessageEditDiffItem;
}>();

const { t } = useI18n();

const getEditorName = (editor: any) =>
  editor?.name ||
  editor?.available_name ||
  t('CONVERSATION.EDIT_HISTORY.SYSTEM');

const isDeletionEvent = computed(() => {
  const isContentSame = props.item.from.content === props.item.to.content;
  const fromCount = props.item.from.attachments?.length || 0;
  const toCount = props.item.to.attachments?.length || 0;

  return isContentSame && fromCount === toCount;
});
</script>

<template>
  <div
    class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 shadow-sm"
  >
    <div class="flex items-center gap-2 mb-3">
      <Thumbnail
        :src="item.editor?.thumbnail || item.editor?.avatar_url || undefined"
        :username="getEditorName(item.editor)"
        size="20px"
        class="rounded-full"
      />
      <span class="text-xs font-medium text-slate-700 dark:text-slate-200">
        {{ getEditorName(item.editor) }}
      </span>
      <span class="text-xs text-slate-400">•</span>
      <span class="text-xs text-slate-500">
        {{ messageTimestamp(item.created_at, 'LLL d, h:mm a') }}
      </span>
    </div>

    <div
      v-if="isDeletionEvent"
      class="flex items-center gap-2 text-red-500 bg-red-50 dark:bg-red-900/10 p-3 rounded-md border border-red-100 dark:border-red-900/20"
    >
      <span class="text-sm font-medium">
        {{ t('CONVERSATION.MESSAGE_DELETED') }}
      </span>
    </div>

    <template v-else>
      <div v-if="item.from.content != item.to.content" class="space-y-2">
        <div class="opacity-60">
          <HistoryMessageContent :content="item.from.content" />
        </div>
        <HistoryMessageContent :content="item.to.content" />
      </div>

      <div v-else class="italic text-slate-400 text-sm leading-relaxed mb-2">
        {{ t('CONVERSATION.EDIT_HISTORY.CONTENT_UNCHANGED') }}
      </div>

      <HistoryAttachments
        :from-attachments="item.from.attachments"
        :to-attachments="item.to.attachments"
      />
    </template>
  </div>
</template>
